import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  orderId: "",
  productId: "",
  rating: 0,
  review: "",
  customerId: "",
  createdDate: "",
  updatedDate: ""
});

const formatDateForInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export function ProductReviewsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");

  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadLookups = async () => {
    setLoading(true);
    setError(null);
    const [oRes, pRes, cRes] = await Promise.all([apiGet("/api/Orders"), apiGet("/api/Products"), apiGet("/api/Customers")]);
    if (oRes.ok) setOrders(oRes.data);
    if (pRes.ok) setProducts(pRes.data);
    if (cRes.ok) setCustomers(cRes.data);
    if (!oRes.ok) setError(oRes.error ?? "Failed to load orders");
    else if (!pRes.ok) setError(pRes.error ?? "Failed to load products");
    else if (!cRes.ok) setError(cRes.error ?? "Failed to load customers");
    setLoading(false);
  };

  useEffect(() => {
    void loadLookups();
  }, []);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await apiGet(`/api/ProductReviews/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product review");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        orderId: x.orderId ?? "",
        productId: x.productId ?? "",
        rating: x.rating ?? 0,
        review: x.review ?? "",
        customerId: x.customerId ?? "",
        createdDate: formatDateForInput(x.createdDate),
        updatedDate: formatDateForInput(x.updatedDate)
      });
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSearchParams({}, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.orderId || !form.productId || !form.customerId) return;
    setSaving(true);
    setError(null);
    const res = await apiPost("/api/ProductReviews", {
      id: editingId ?? 0,
      orderId: Number(form.orderId),
      productId: Number(form.productId),
      rating: Number(form.rating || 0),
      review: form.review || null,
      customerId: Number(form.customerId),
      createdDate: form.createdDate || new Date().toISOString(),
      updatedDate: form.updatedDate || null
    });
    if (!res.ok) setError(res.error ?? "Failed to save product review");
    else {
      await loadLookups();
      resetForm();
    }
    setSaving(false);
  };

  const onRefresh = async () => {
    setError(null);
    await loadLookups();
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`/api/ProductReviews/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product review");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        orderId: x.orderId ?? "",
        productId: x.productId ?? "",
        rating: x.rating ?? 0,
        review: x.review ?? "",
        customerId: x.customerId ?? "",
        createdDate: formatDateForInput(x.createdDate),
        updatedDate: formatDateForInput(x.updatedDate)
      });
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>{editingId ? "Edit Product Review" : "Add Product Review"}</h2>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="submit"
            form="product-reviews-form"
            disabled={saving}
            title="Save"
            aria-label="Save product review"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefresh()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/product-reviews-list" className="icon-button icon-button--toolbar" title="Product reviews list" aria-label="Open list">
            <IconList />
          </Link>
        </div>
      </div>
      <form id="product-reviews-form" onSubmit={onSubmit} className="card-body form form-fields-bottomline">
        <label className="field">
          <span>Order</span>
          <select value={form.orderId} onChange={(e) => setForm((p) => ({ ...p, orderId: e.target.value ? Number(e.target.value) : "" }))}>
            <option value="">Select order</option>
            {orders.map((o) => (
              <option key={o.id} value={o.id}>
                {o.orderNo || `Order #${o.id}`}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Product</span>
          <select value={form.productId} onChange={(e) => setForm((p) => ({ ...p, productId: e.target.value ? Number(e.target.value) : "" }))}>
            <option value="">Select product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || `Product #${p.id}`}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Customer</span>
          <select value={form.customerId} onChange={(e) => setForm((p) => ({ ...p, customerId: e.target.value ? Number(e.target.value) : "" }))}>
            <option value="">Select customer</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || `Customer #${c.id}`}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Rating</span>
          <input type="number" value={form.rating} onChange={(e) => setForm((p) => ({ ...p, rating: e.target.value }))} min={0} max={5} />
        </label>
        <label className="field">
          <span>Review</span>
          <textarea rows={3} value={form.review} onChange={(e) => setForm((p) => ({ ...p, review: e.target.value }))} />
        </label>
        <label className="field">
          <span>Created Date</span>
          <input type="datetime-local" value={form.createdDate} onChange={(e) => setForm((p) => ({ ...p, createdDate: e.target.value }))} />
        </label>
        <label className="field">
          <span>Updated Date</span>
          <input type="datetime-local" value={form.updatedDate} onChange={(e) => setForm((p) => ({ ...p, updatedDate: e.target.value }))} />
        </label>
        {error && <div className="error-banner">{error}</div>}
      </form>
    </div>
  );
}
